import { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight, Calendar, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const ExitIntentModal = () => {
  const [show, setShow] = useState(false);

  const handleMouseLeave = useCallback((e) => {
    if (e.clientY <= 5 && !sessionStorage.getItem('exit_intent_shown')) {
      setShow(true);
      sessionStorage.setItem('exit_intent_shown', 'true');
    }
  }, []);

  useEffect(() => {
    // Desktop: mouse leave
    document.addEventListener('mouseleave', handleMouseLeave);

    // Mobile: back-scroll to top after scrolling down
    let maxScroll = 0;
    const handleScroll = () => {
      const y = window.scrollY;
      if (y > maxScroll) maxScroll = y;
      if (maxScroll > 600 && y < maxScroll * 0.3 && !sessionStorage.getItem('exit_intent_shown')) {
        setShow(true);
        sessionStorage.setItem('exit_intent_shown', 'true');
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleMouseLeave]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="exit-intent-overlay"
      onClick={() => setShow(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative w-full max-w-md bg-[#0c0c14] border border-slate-800 rounded-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300 mx-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-modal"
      >
        {/* Top accent */}
        <div className="h-1 bg-gradient-to-r from-blue-600 via-orange-500 to-blue-600" />

        {/* Close */}
        <button
          onClick={() => setShow(false)}
          className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
          data-testid="exit-intent-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500/10 border border-orange-500/20 rounded-full mb-5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
            </span>
            <span className="text-[11px] font-semibold text-orange-400 uppercase tracking-wider">Live Webinar</span>
          </div>

          {/* Headline */}
          <h2 className="font-heading text-2xl font-bold text-white mb-2 leading-tight">
            Before you go —<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-300">
              this might change how your team works.
            </span>
          </h2>

          <p className="text-sm text-slate-400 mb-6 leading-relaxed">
            Join Furqan (CEO, Plouton AI) for a free 60-min session on AI for finance — Claude & GPT best practices, practical LLM workflows, and a live agent demo.
          </p>

          {/* Date/time */}
          <div className="flex items-center justify-center gap-4 text-xs text-slate-500 mb-6">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              June 21, 2026
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              12:00 PM EST
            </span>
          </div>

          {/* CTA */}
          <Link to="/webinar" onClick={() => setShow(false)}>
            <Button
              className="w-full bg-orange-500 text-white hover:bg-orange-600 font-semibold rounded-lg py-5 text-base transition-all"
              data-testid="exit-intent-cta"
            >
              Save My Seat — Free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>

          <button
            onClick={() => setShow(false)}
            className="mt-4 text-xs text-slate-600 hover:text-slate-400 transition-colors"
          >
            No thanks, I'll skip this one
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentModal;
